import type { Timestamp } from 'firebase/firestore'

/**
 * One organisation's record of who was in a car, and from when.
 *
 * An overlay rather than a shared fact. A declared driver is not observable:
 * §8b of docs/timing-pipeline.md records that even the vendor's own `driverId`
 * is the team telling the timekeeper, applied when told rather than when it
 * happened — one observed change landed on a lap of entirely normal pace,
 * which is impossible without a stop. So this is an assertion by whoever made
 * it, and is stored as theirs.
 *
 * Kept at streamSessions/{id}/declarations/{orgId}, readable and writable only
 * by that organisation. Two teams may hold different beliefs about the same car
 * without either having to claim ownership of it, which removes the only part
 * of this that would have needed adjudicating.
 */
export interface DeclarationDoc {
  /** Duplicated from the path so a collection-group query can filter on it. */
  orgId: string
  /**
   * Append-only, and not necessarily in order — a correction is a later entry,
   * not an edit — so anything reading this must sort rather than assume.
   */
  changes: DriverChange[]
  updatedAt: Timestamp
}

export interface DriverChange {
  /**
   * The car, exactly as the timing screen shows it. Compared literally after
   * trimming: "7" and "07" are different cars to a timekeeper, so normalising
   * away a leading zero would silently merge two entries.
   */
  car: string
  /**
   * A driver from the organisation's own list, or null for "we do not know who
   * from here" — which is a real thing to say, and is not the same as saying
   * nothing. A null hands the question back to the vendor rather than asserting
   * an empty answer.
   */
  driverId: string | null
  /** When they got in. */
  from: Timestamp
}

/** What a change looks like once read back, with the timestamp resolved. */
export interface Change {
  car: string
  driverId: string | null
  from: Date
}

/**
 * Who was in a car at a moment, and on whose authority.
 *
 * Three outcomes rather than a nullable string, so a caller cannot accidentally
 * render a vendor's guess as though the team had said it. Where the two differ
 * it matters which is being shown.
 */
export type ResolvedDriver =
  | { source: 'team'; driverId: string }
  | { source: 'vendor'; name: string }
  | { source: 'none' }

/**
 * Resolve who was in `car` at `at`.
 *
 * The team's declaration wins where one covers the moment, and the vendor fills
 * the gap. That ordering is not deference to whoever is closer to the car — it
 * is that the vendor's value is the same claim arriving later, routed through a
 * timekeeper who applies it when told.
 *
 * `vendorDriver` is whatever the captured data said for that lap, which for RIS
 * is often nothing at all.
 */
export function resolveDriver(
  changes: readonly Change[],
  car: string,
  at: Date,
  vendorDriver?: string | null,
): ResolvedDriver {
  const applicable = changes
    .filter((c) => sameCar(c.car, car) && c.from.getTime() <= at.getTime())
    // Stable, so that two changes recorded at the same instant — which is what
    // an immediate correction looks like — resolve to the one appended later.
    .sort((a, b) => a.from.getTime() - b.from.getTime())

  const latest = applicable[applicable.length - 1]
  if (latest?.driverId) return { source: 'team', driverId: latest.driverId }

  // Either nothing was declared, or the most recent declaration is an explicit
  // "we do not know". Both hand the question to the vendor.
  const name = vendorDriverName(vendorDriver)
  return name ? { source: 'vendor', name } : { source: 'none' }
}

/**
 * Every stint the declarations describe for a car, as closed intervals.
 *
 * This is what stint analysis reads, and it is why the declarations are a time
 * series rather than a current value: "who is in the car" is answerable from a
 * single field, but "how did each driver's pace compare" is not.
 *
 * The last stint is left open — `until` undefined — because a session that is
 * still running has no end for it, and inventing one would make a live stint
 * look finished.
 */
export function stints(
  changes: readonly Change[],
  car: string,
): { driverId: string | null; from: Date; until?: Date }[] {
  const forCar = changes
    .filter((c) => sameCar(c.car, car))
    .sort((a, b) => a.from.getTime() - b.from.getTime())

  return forCar.map((c, i) => {
    const next = forCar[i + 1]
    return next ? { driverId: c.driverId, from: c.from, until: next.from } : { driverId: c.driverId, from: c.from }
  })
}

function sameCar(a: string, b: string): boolean {
  return a.trim() === b.trim()
}

/**
 * The vendor's driver name, where it is actually a name.
 *
 * Blank is the obvious non-answer. The other one is a bare asterisk, which is
 * what /funfr sent for every car of the Le Castellet weekend — a placeholder
 * for "a driver, unspecified", not somebody called *. Read literally it makes
 * the feed look like it is tracking driver changes when it is not, which is the
 * difference between asking who got in the car and not needing to.
 *
 * One definition, in one place, because every reader of a vendor driver name
 * has to agree about this or they disagree about whether a stint has a driver.
 */
export function vendorDriverName(raw: string | null | undefined): string | null {
  const name = raw?.trim()
  if (!name || name === '*') return null
  return name
}

/**
 * Which of the team's drivers a vendor's abbreviated name is.
 *
 * Timing screens print names their own way — RIS gives "T.QUA" for a Tom
 * Quarendon — and the race plan prints the roster's. A dashboard showing one
 * form above and the other below reads as two different people, so where the
 * vendor's form can be pinned to exactly one roster entry, that entry's name
 * is used instead.
 *
 * A match is: the roster driver's short name spelled the same, or every piece
 * of the vendor's name being the start of a distinct piece of the roster
 * name ("T" of Tom, "QUA" of Quarendon). Case and accents are ignored. A
 * single initial on its own is never enough, and two candidates is no answer
 * at all: a wrong name on the pit wall is worse than the vendor's own.
 *
 * The id, not the entry, because the caller already has the roster.
 */
export function matchVendorDriver(
  vendorName: string | null | undefined,
  roster: readonly { id: string; name: string; shortName?: string | null }[],
): string | null {
  const want = pieces(vendorDriverName(vendorName))
  if (want.length === 0) return null

  const exact = roster.filter((d) => {
    const short = pieces(d.shortName)
    return short.length > 0 && short.join(' ') === want.join(' ')
  })
  if (exact.length === 1) return exact[0]!.id
  if (exact.length > 1) return null

  // One initial matches half the grid. Insist on some of a surname.
  if (!want.some((p) => p.length >= 2)) return null

  const found = roster.filter((d) => coveredBy(want, pieces(d.name)))
  return found.length === 1 ? found[0]!.id : null
}

/** Letters only, accent-stripped, upper-cased, split on everything else. */
function pieces(name: string | null | undefined): string[] {
  if (!name) return []
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .split(/[^A-Z]+/)
    .filter(Boolean)
}

/** Every wanted piece starts a different piece of the name. */
function coveredBy(want: readonly string[], have: readonly string[]): boolean {
  const left = [...have]
  for (const w of want) {
    const i = left.findIndex((h) => h.startsWith(w))
    if (i < 0) return false
    left.splice(i, 1)
  }
  return true
}
